import React, { ReactNode } from 'react';
import { UseQueryResult } from '@tanstack/react-query';

import { InlineError, Spinner } from 'design-system/react';

export interface QueryResultProps<TData, TError extends Error> {
  query: UseQueryResult<TData, TError>;
  loadingAs?: React.ComponentType;
  errorAs?: (error: TError) => ReactNode;
  renderAs: (data: TData) => ReactNode;
  direction?: 'column' | 'row';
  justifyContent?: 'start' | 'end';
  debug?: boolean;
}

function DefaultSpinner() {
  return (
    <div role="status">
      <Spinner size="lg" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

export function QueryResult<TData, TError extends Error>({
  query,
  loadingAs,
  errorAs,
  renderAs,
  direction,
  justifyContent,
  debug,
}: QueryResultProps<TData, TError>) {
  const LoadingComponent = loadingAs || DefaultSpinner;

  if (debug) {
    console.log(
      'QueryResult',
      'isLoading:',
      query.isLoading,
      'isError:',
      query.isError,
      'data:',
      query.data !== undefined,
      'error:',
      query.error,
    );
  }

  const dirClass = direction === 'row' ? 'flex-row' : 'flex-col';
  const justifyClass = justifyContent === 'end' ? 'justify-end' : justifyContent === 'start' ? 'justify-start' : '';

  return (
    <div className={`flex ${dirClass} w-full items-center ${justifyClass}`}>
      {query.isLoading && <LoadingComponent />}
      {query.isError && (errorAs !== undefined ? errorAs(query.error) : <InlineError error={query.error.message} />)}
      {query.data !== undefined && renderAs(query.data)}
    </div>
  );
}
